import React, { useEffect, useState } from 'react'
import compose from 'recompose/compose'
import { withI18n } from '../../hocs'
import * as S from './styled'
import ActionDialog from '../action-dialog'
import { patchUser } from '../../../api/index';
import { useMutation } from "@tanstack/react-query";
import { useAppContext } from '../../../context/appContext'
import { ALERT_LEVEL } from '../../../config/constants'

const ModalDisableUser = ({ open, handleClose, i18n, userData, currentStatus, onDataUpdate, onRefresh, selectedRows }) => {
  const { createAlert } = useAppContext()
  const [toggleCurrentStatus, setToggleCurrentStatus] = useState(null);
  const [userDataState, setUserDataState] = useState(null);
  const [selectedRowsState, setSelectedRowsState] = useState([]);

  useEffect(() => {
    if (open) {
      const newCurrentStatus = userData ? userData.is_active : false;

      setToggleCurrentStatus(newCurrentStatus);
      setUserDataState(userData);
      setSelectedRowsState(selectedRows);
    }
  }, [open, userData, selectedRows]);


  // Sin tragarse el error: si falla, el modal no se cierra ni sale el aviso de éxito
  const changeStatusUser = async (data) => {
    const response = await patchUser(data.id, data);
    return response.data;
  }

  const mutationChangeStatusUser = useMutation(changeStatusUser);

  const areAllUsersActive = selectedRows.every(row => row.is_active);
  const areAllUsersInactive = selectedRows.every(row => !row.is_active);

  // El modal se cierra cuando ha ido bien y la tabla ya está refrescada; el aviso de éxito lo da la página
  const handleClickDisable = async (e) => {
    e.preventDefault();

    try {
      if (userDataState && userDataState.id) {
        await mutationChangeStatusUser.mutateAsync({
          id: userDataState.id,
          is_active: !toggleCurrentStatus
        });
      } else if (selectedRows.length > 0) {
        const promises = selectedRows.map((row) => {
          const newUserStatus = toggleCurrentStatus ? !areAllUsersInactive : !areAllUsersActive;
          return mutationChangeStatusUser.mutateAsync(
            {
              id: row.id,
              is_active: newUserStatus
            },
            {
              onError: (error) => {
                console.error(`Error en la mutación para el usuario ${row.id}:`, error);
              },
            }
          );
        });

        // Se espera a todas: si alguna falla, la tabla se refresca igual con las que sí han cambiado
        // y el modal sigue abierto con el aviso de error
        const results = await Promise.allSettled(promises);
        if (results.some(result => result.status === 'rejected')) {
          if (onRefresh && results.some(result => result.status === 'fulfilled')) await onRefresh();
          throw new Error('Some users could not be updated');
        }
      } else {
        return;
      }
      await onDataUpdate();
      handleClose();
    } catch (error) {
      console.error(error);
      const isBulk = selectedRowsState.length > 0;
      createAlert(isDisabling
        ? (isBulk ? i18n.user_management.usersDisabledError : i18n.user_management.userDisabledError)
        : (isBulk ? i18n.user_management.usersEnabledError : i18n.user_management.userEnabledError), ALERT_LEVEL.ERROR);
    }
  };

  const handleModalClose = () => {
    setToggleCurrentStatus(null);
    setUserDataState(null);
    setSelectedRowsState([]);
    handleClose();
  };

  // Usuarios a los que afecta: el de la fila o los seleccionados. Con uno solo el nombre va en la
  // pregunta (venga de la fila o de la selección); con varios, pregunta en plural y lista debajo.
  const targets = userDataState && userDataState.id ? [userDataState] : selectedRowsState
  const isSingle = targets.length === 1

  // Desactivar es destructivo; en masivo el sentido lo marca si todos los seleccionados están activos
  const isDisabling = userDataState && userDataState.id ? !!toggleCurrentStatus : areAllUsersActive

  const getTitleForConfirmation = () => {
    if (!targets.length) return '';
    if (isSingle) {
      const [user] = targets
      return (isDisabling ? i18n.user_management.confirmDeactivation : i18n.user_management.confirmActivation)
        .replace('{name}', `${user.name || ''}`)
        .replace('{surname}', `${user.surname || ''}`)
        .replace(/\s+\?$/, '?');
    }
    return isDisabling ? i18n.user_management.confirmDisableUsers : i18n.user_management.confirmEnableUsers;
  };

  const getTitleForButton = () => {
    if (!targets.length) return '';
    if (isSingle) {
      return isDisabling ? i18n.user_management.buttonConfirmDisable : i18n.user_management.buttonConfirmEnable;
    }
    return isDisabling ? i18n.user_management.buttonConfirmDisableUsers : i18n.user_management.buttonConfirmEnableUsers;
  };

  return (
    <ActionDialog
      open={open}
      title={getTitleForConfirmation()}
      onClose={handleModalClose}
      onConfirm={handleClickDisable}
      cancelLabel={i18n.dialog.decline}
      confirmLabel={getTitleForButton()}
      confirmColor={isDisabling ? 'error' : 'default'}
      isPending={mutationChangeStatusUser.isLoading}
    >
      {targets.length > 1 && (
        <S.Names>
          {targets.map((row) => (
            <li key={row.id}>{row.name} {row.surname}</li>
          ))}
        </S.Names>
      )}
    </ActionDialog>
  )
}

const enharce = compose(withI18n)

export default enharce(ModalDisableUser)
